import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, Play, Trophy, Users, RotateCcw, Zap } from "lucide-react";

// ---------------------- Track geometry ----------------------
const W = 480;                    // canvas viewport width
const H = 720;                    // canvas viewport height
const WORLD_H = 6800;             // full track length (scrolls with camera)
const ORB_R = 6;
const GRAVITY = 0.095;            // moderate — pegs deflect but orbs still make progress
const DAMP = 0.998;               // extra-low friction so bounces really carry
const MAX_V = 6.2;                // higher terminal for chaotic pack, races 30-40s
const MAX_UP = -4.0;              // allow bigger upward pops for drama
const SUBSTEPS = 3;
const FINISH_Y = WORLD_H - 60;
const HOLE_TRAP_TICKS = 22;       // frames the orb is "in" a hole before it exits

// Seeded RNG — Mulberry32 (deterministic across clients)
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------- Tracks ----------------------
// Each track builds a long vertical course made of:
//   pegs    — small collision circles that scatter orbs
//   walls   — line segments (ramps, chutes, funnels)
//   bumpers — big circular pop-bumpers that add extra kick + points
//   holes   — warp pits: orbs that fall in disappear for HOLE_TRAP_TICKS
//             frames then re-emerge X px lower on a deterministic-random x
function buildTrack(kind) {
  const pegs = [];
  const bumpers = [];
  const holes = [];
  const spinners = [];   // rotating bar obstacles (deterministic per tick)
  const walls = [
    // outer left / right walls the full length of the world
    { x1: 10, y1: 0, x2: 10, y2: WORLD_H, r: 0.5 },
    { x1: W - 10, y1: 0, x2: W - 10, y2: WORLD_H, r: 0.5 },
  ];

  // ---- helpers ----
  const pegRow = (y, cols, xStart, xEnd, r = 7) => {
    for (let i = 0; i < cols; i++) {
      const t = cols === 1 ? 0.5 : i / (cols - 1);
      pegs.push({ x: xStart + t * (xEnd - xStart), y, r });
    }
  };
  const bumper = (x, y, r, color, points = 100) => bumpers.push({ x, y, r, color, points });
  const hole = (x, y, r, warpDy, motion) => holes.push({ x, y, r, warpDy, motion });
  const movingWall = (x1, y1, x2, y2, restitution, amp, freq, phase) =>
    walls.push({ x1, y1, x2, y2, r: restitution, motion: { amp, freq, phase } });
  // speed = radians per tick. Positive = clockwise.
  const spinner = (cx, cy, len, speed, phase, color, thickness = 6) =>
    spinners.push({ cx, cy, len, speed, phase, color, thickness, r: 1.0 });

  if (kind === "classic") {
    // Section 1 (300-840): DENSE peg field forces immediate scatter
    for (let row = 0; row < 10; row++) {
      const y = 300 + row * 54;
      const off = row % 2 === 0 ? 0 : 22;
      pegRow(y, 11, 40 + off, W - 40 - off, 7);
    }
    // Section 2 (900-1150): triangle bumpers + kicker
    bumper(130, 950, 24, "#FF3366", 150);
    bumper(350, 950, 24, "#00E5FF", 150);
    bumper(240, 1080, 28, "#CCFF00", 200);
    // Warp hole (1250)
    hole(240, 1280, 22, 520);
    // Section 3 (1400-1900): center-sinusoid peg field with side pegs
    for (let row = 0; row < 8; row++) {
      const y = 1440 + row * 62;
      const cx = W / 2 + Math.sin(row * 0.9) * 100;
      pegs.push({ x: cx, y, r: 7 });
      pegs.push({ x: cx - 60, y: y + 18, r: 6 });
      pegs.push({ x: cx + 60, y: y + 18, r: 6 });
      pegs.push({ x: cx - 120, y: y + 5, r: 6 });
      pegs.push({ x: cx + 120, y: y + 5, r: 6 });
    }
    // Section 4 (2000-2350): bumper trio + side warp holes
    bumper(140, 2020, 22, "#a855f7", 120);
    bumper(340, 2020, 22, "#a855f7", 120);
    bumper(240, 2160, 26, "#F59E0B", 180);
    hole(65, 2280, 22, 400);
    hole(415, 2280, 22, 400);
    // Section 5 (2500-3200): slalom ramps + DENSE peg scatter
    walls.push({ x1: 10, y1: 2500, x2: W - 160, y2: 2650, r: 0.7 });
    walls.push({ x1: W - 10, y1: 2760, x2: 160, y2: 2910, r: 0.7 });
    walls.push({ x1: 10, y1: 3020, x2: W - 160, y2: 3170, r: 0.7 });
    for (let i = 0; i < 22; i++) pegs.push({ x: 40 + (i * 41) % (W - 80), y: 2560 + i * 32, r: 6 });
    // Section 6 (3300-3600): center warp hole + bumper wall
    bumper(90, 3350, 22, "#22d3ee", 130);
    bumper(390, 3350, 22, "#22d3ee", 130);
    hole(240, 3480, 26, 560);
    // Section 7 (3700-4400): DENSE peg field + bumpers
    for (let row = 0; row < 9; row++) pegRow(3720 + row * 58, 10, 45, W - 45, 7);
    bumper(150, 4200, 24, "#FF3366", 150);
    bumper(330, 4200, 24, "#00E5FF", 150);
    bumper(240, 4350, 28, "#CCFF00", 200);
    // Section 8 (4500-4900): S-curve zigzag with side pegs
    for (let row = 0; row < 8; row++) {
      const y = 4520 + row * 60;
      const cx = W / 2 + Math.sin(row * 0.7) * 130;
      pegs.push({ x: cx, y, r: 7 });
      pegs.push({ x: cx - 65, y: y + 22, r: 6 });
      pegs.push({ x: cx + 65, y: y + 22, r: 6 });
      pegs.push({ x: cx - 130, y: y + 8, r: 6 });
      pegs.push({ x: cx + 130, y: y + 8, r: 6 });
    }
    // Section 9 (5000-5300): mid-course warp + bumper
    hole(120, 5020, 22, 420);
    hole(360, 5020, 22, 420);
    bumper(240, 5150, 30, "#F59E0B", 200);
    // Section 10 (5350-5900): final slalom with dense scatter
    walls.push({ x1: W - 10, y1: 5350, x2: 160, y2: 5500, r: 0.7 });
    walls.push({ x1: 10, y1: 5620, x2: W - 160, y2: 5770, r: 0.7 });
    for (let i = 0; i < 18; i++) pegs.push({ x: 40 + (i * 47) % (W - 80), y: 5400 + i * 32, r: 6 });
    // Section 11 (6000-6700): funnel finish
    walls.push({ x1: 10, y1: 6000, x2: 140, y2: 6300, r: 0.6 });
    walls.push({ x1: W - 10, y1: 6000, x2: W - 140, y2: 6300, r: 0.6 });
    walls.push({ x1: 140, y1: 6300, x2: 180, y2: 6600, r: 0.6 });
    walls.push({ x1: W - 140, y1: 6300, x2: W - 180, y2: 6600, r: 0.6 });
    bumper(W / 2, 6450, 26, "#a855f7", 200);
  } else if (kind === "zigzag") {
    // S-curve slalom: alternating half-width ramps with clear gaps.
    // 12 stages fill the extended world for a proper long-form race.
    const stages = 12;
    const gap = 150;
    const spacing = 500;
    for (let s = 0; s < stages; s++) {
      const y = 260 + s * spacing;
      const leftEnd = s % 2 === 0;
      if (leftEnd) {
        walls.push({ x1: W - 10, y1: y, x2: gap, y2: y + 220, r: 0.7 });
        pegs.push({ x: gap + 30, y: y + 300, r: 7 });
        bumper(W - 80, y + 340, 22, s % 3 === 0 ? "#FF3366" : "#00E5FF", 130);
      } else {
        walls.push({ x1: 10, y1: y, x2: W - gap, y2: y + 220, r: 0.7 });
        pegs.push({ x: W - gap - 30, y: y + 300, r: 7 });
        bumper(80, y + 340, 22, s % 3 === 0 ? "#CCFF00" : "#a855f7", 130);
      }
      // Warp hole in the free-fall zone (every 3rd stage)
      if (s % 3 === 1) {
        hole(W / 2 + (leftEnd ? -60 : 60), y + 400, 22, 380);
      }
      // DENSE peg scatter in the free-fall zone between ramps so orbs
      // can't just plummet straight through — they must bounce sideways.
      const zoneY = y + 250;
      pegRow(zoneY, 5, 60, W - 60, 7);
      pegRow(zoneY + 60, 6, 40, W - 40, 6);
      pegRow(zoneY + 120, 5, 60, W - 60, 7);
      pegRow(zoneY + 180, 6, 40, W - 40, 6);
      // Also a center peg near the exit
      pegs.push({ x: W / 2 + (leftEnd ? 40 : -40), y: y + 400, r: 7 });
    }
    // Final funnel to finish
    walls.push({ x1: 10, y1: 6300, x2: 180, y2: 6600, r: 0.6 });
    walls.push({ x1: W - 10, y1: 6300, x2: W - 180, y2: 6600, r: 0.6 });
    bumper(W / 2, 6450, 28, "#F59E0B", 200);
  } else if (kind === "funnel") {
    // 4 chained funnels of decreasing width, with bumpers at each choke and
    // holes flanking the sides. Fills the extended world height.
    const stages = [
      { yTop: 260,  yBot: 1500, chokePad: 130 },
      { yTop: 1620, yBot: 2900, chokePad: 155 },
      { yTop: 3020, yBot: 4300, chokePad: 175 },
      { yTop: 4420, yBot: 5700, chokePad: 195 },
    ];
    stages.forEach((st, idx) => {
      walls.push({ x1: 10, y1: st.yTop, x2: st.chokePad, y2: st.yBot, r: 0.75 });
      walls.push({ x1: W - 10, y1: st.yTop, x2: W - st.chokePad, y2: st.yBot, r: 0.75 });
      // choke bumpers
      bumper(st.chokePad + 30, st.yBot - 60, 22, "#FF3366", 140);
      bumper(W - st.chokePad - 30, st.yBot - 60, 22, "#00E5FF", 140);
      bumper(W / 2, st.yBot - 140, 26, ["#CCFF00", "#F59E0B", "#a855f7", "#22d3ee"][idx], 200);
      // dense pegs inside the funnel
      for (let row = 0; row < 6; row++) {
        const y = st.yTop + 100 + row * 130;
        const cols = 5 + row % 2;
        const inset = 55 + (row * 8);
        pegRow(y, cols, inset, W - inset, 5);
      }
      // Extra dense scatter rows below the choke to break up the pack
      pegRow(st.yBot + 25, 5, 100, W - 100, 6);
      pegRow(st.yBot + 65, 4, 140, W - 140, 6);
      // hole at exit sides
      hole(st.chokePad - 6, st.yBot - 20, 22, 260);
      hole(W - st.chokePad + 6, st.yBot - 20, 22, 260);
    });
    // Final chute: tight tube with a center bumper
    walls.push({ x1: W / 2 - 100, y1: 5800, x2: W / 2 - 70, y2: 6600, r: 0.6 });
    walls.push({ x1: W / 2 + 100, y1: 5800, x2: W / 2 + 70, y2: 6600, r: 0.6 });
    bumper(W / 2, 6100, 26, "#FF3366", 200);
    bumper(W / 2 - 60, 6300, 18, "#00E5FF", 120);
    bumper(W / 2 + 60, 6300, 18, "#00E5FF", 120);
  }

  // ---- Add "boom" elements to every track: spinners, moving platforms,
  // and drifting teleporters. Each is deterministic (function of tick).
  if (kind === "classic") {
    // Rotating spinner bars at key choke points
    spinner(W / 2, 1180, 100, 0.045, 0, "#00E5FF");
    spinner(W / 2, 3600, 120, -0.055, 0.6, "#FF3366");
    spinner(W / 2, 5200, 90, 0.06, 1.4, "#CCFF00");
    // Sliding platform in the mid-section
    movingWall(140, 2900, 340, 2900, 0.9, 90, 0.05, 0);
    movingWall(140, 5000, 340, 5000, 0.9, 100, -0.045, 1.2);
    // Drifting teleporter
    holes[0] && (holes[0].motion = { amp: 90, freq: 0.02, phase: 0 });      // 240,1280
    // Add a second drifting teleporter
    hole(W / 2, 4650, 22, 500, { amp: 130, freq: 0.025, phase: 0.9 });
  } else if (kind === "zigzag") {
    // Two spinners at critical crossings
    spinner(W / 2, 1500, 110, 0.05, 0, "#FF3366");
    spinner(W / 2, 3500, 110, -0.05, 1.2, "#00E5FF");
    spinner(W / 2, 5500, 110, 0.06, 2.4, "#CCFF00");
    // Oscillating horizontal wall — a moving "guard"
    movingWall(120, 2300, 360, 2300, 0.85, 100, 0.04, 0);
    movingWall(120, 4300, 360, 4300, 0.85, 100, -0.04, 1.7);
    // Drifting teleporters — animate existing holes
    holes.forEach((h, i) => {
      if (i < 3) h.motion = { amp: 70, freq: 0.025 + i * 0.005, phase: i * 0.8 };
    });
  } else if (kind === "funnel") {
    // Spinner at the throat of each funnel stage
    spinner(W / 2, 1450, 130, 0.05, 0, "#FF3366");
    spinner(W / 2, 2850, 120, -0.06, 0.7, "#00E5FF");
    spinner(W / 2, 4250, 110, 0.07, 1.5, "#CCFF00");
    spinner(W / 2, 5650, 100, -0.08, 2.3, "#F59E0B");
    // Small drifting teleporters
    holes.forEach((h, i) => {
      h.motion = { amp: 40, freq: 0.03 + (i % 3) * 0.008, phase: i * 0.55 };
    });
  }

  return { pegs, walls, bumpers, holes, spinners };
}

// ---------------------- Simulation ----------------------
function simulate({ seed, track, participants }) {
  const rng = mulberry32(seed);
  const { pegs, walls, bumpers, holes, spinners } = buildTrack(track);
  const orbs = participants.map((p, i) => {
    const row = Math.floor(i / 18);
    const col = i % 18;
    return {
      peer_id: p.peer_id,
      username: p.username,
      color: p.color,
      x: 30 + col * ((W - 60) / 17) + (rng() - 0.5) * 8,
      y: 30 + row * 18,
      vx: (rng() - 0.5) * 5.5,
      vy: rng() * 1.5,
      finished: false,
      finishedAt: 0,
      trapped: 0,
      trapExit: null,
      lastBumper: -999,
      bumperHits: 0,
    };
  });
  const holeRng = mulberry32(seed ^ 0x9E3779B9);
  return { pegs, walls, bumpers, holes, spinners, orbs, tick: 0, holeRng };
}

function step(state, onFinish) {
  const { pegs, walls, bumpers, holes, spinners, orbs, holeRng } = state;
  for (let s = 0; s < SUBSTEPS; s++) {
    // Effective tick used for animating moving elements — smooth per substep
    const tSub = state.tick + s / SUBSTEPS;
    // Precompute animated wall endpoints and hole positions (per substep,
    // shared by every orb). Result stored on the wall/hole objects.
    for (const w of walls) {
      if (w.motion) {
        const off = Math.sin(tSub * w.motion.freq + w.motion.phase) * w.motion.amp;
        w._x1 = w.x1 + off; w._y1 = w.y1;
        w._x2 = w.x2 + off; w._y2 = w.y2;
        w._vx = Math.cos(tSub * w.motion.freq + w.motion.phase) * w.motion.freq * w.motion.amp;
        w._vy = 0;
      } else {
        w._x1 = w.x1; w._y1 = w.y1; w._x2 = w.x2; w._y2 = w.y2; w._vx = 0; w._vy = 0;
      }
    }
    for (const h of holes) {
      if (h.motion) {
        h._x = h.x + Math.sin(tSub * h.motion.freq + h.motion.phase) * h.motion.amp;
      } else {
        h._x = h.x;
      }
    }
    // Spinners: rotating bars around a pivot
    for (const sp of spinners) {
      const angle = tSub * sp.speed + sp.phase;
      const cA = Math.cos(angle), sA = Math.sin(angle);
      sp._x1 = sp.cx + cA * sp.len / 2;
      sp._y1 = sp.cy + sA * sp.len / 2;
      sp._x2 = sp.cx - cA * sp.len / 2;
      sp._y2 = sp.cy - sA * sp.len / 2;
      sp._angle = angle;
      sp._omega = sp.speed;    // angular velocity
    }

    for (const o of orbs) {
      if (o.finished) continue;
      if (o.trapped > 0) {
        o.trapped -= 1;
        if (o.trapped === 0 && o.trapExit) {
          o.x = o.trapExit.x;
          o.y = o.trapExit.y;
          o.vx = (holeRng() - 0.5) * 2;
          o.vy = 0.5;
          o.trapExit = null;
        }
        continue;
      }
      o.vy += GRAVITY / SUBSTEPS;
      o.vx *= DAMP;
      o.vy *= DAMP;
      if (o.vx > MAX_V) o.vx = MAX_V;
      if (o.vx < -MAX_V) o.vx = -MAX_V;
      if (o.vy > MAX_V) o.vy = MAX_V;
      if (o.vy < MAX_UP) o.vy = MAX_UP;
      o.x += o.vx / SUBSTEPS;
      o.y += o.vy / SUBSTEPS;

      // Walls (including moving platforms) — reflect off the CURRENT position.
      // If the wall is moving, add its velocity to the collision impulse so it
      // slams the orb (like a paddle).
      for (const w of walls) {
        const abx = w._x2 - w._x1, aby = w._y2 - w._y1;
        const apx = o.x - w._x1, apy = o.y - w._y1;
        const len2 = abx * abx + aby * aby || 1;
        let t = (apx * abx + apy * aby) / len2;
        t = Math.max(0, Math.min(1, t));
        const cx = w._x1 + abx * t, cy = w._y1 + aby * t;
        const dx = o.x - cx, dy = o.y - cy;
        const d = Math.hypot(dx, dy);
        if (d < ORB_R) {
          const nx = dx / (d || 1), ny = dy / (d || 1);
          // Include wall velocity so moving platforms transfer momentum
          const vn = (o.vx - w._vx) * nx + (o.vy - w._vy) * ny;
          if (vn < 0) {
            const restitution = w.r ?? 0.85;
            const j = (1 + restitution) * vn;
            o.vx -= j * nx;
            o.vy -= j * ny;
            // Extra "slam" impulse from moving platform velocity
            if (w.motion) {
              o.vx += w._vx * 0.9;
              o.vy += w._vy * 0.9;
            }
          }
          o.x = cx + nx * (ORB_R + 0.5);
          o.y = cy + ny * (ORB_R + 0.5);
        }
      }
      // Pegs — very bouncy (r=0.92) with a strong lateral kick to break stalls
      // and force real sideways motion (orbs shouldn't just fall straight down).
      for (const p of pegs) {
        const dx = o.x - p.x, dy = o.y - p.y;
        const d = Math.hypot(dx, dy);
        if (d < ORB_R + p.r) {
          const nx = dx / (d || 1), ny = dy / (d || 1);
          const vn = o.vx * nx + o.vy * ny;
          if (vn < 0) {
            const j = 1.92 * vn;
            o.vx -= j * nx;
            o.vy -= j * ny;
          }
          o.vx += nx > 0 ? 0.85 : -0.85;
          o.x = p.x + nx * (ORB_R + p.r + 0.5);
          o.y = p.y + ny * (ORB_R + p.r + 0.5);
        }
      }
      // Bumpers — high-energy pop-bumpers with strong lateral kick.
      for (const b of bumpers) {
        const dx = o.x - b.x, dy = o.y - b.y;
        const d = Math.hypot(dx, dy);
        if (d < ORB_R + b.r) {
          const nx = dx / (d || 1), ny = dy / (d || 1);
          const vn = o.vx * nx + o.vy * ny;
          if (vn < 0) {
            const j = 2.1 * vn;
            o.vx -= j * nx;
            o.vy -= j * ny;
          }
          o.vx += nx * 2.4;
          o.vy += Math.max(0, ny) * 1.2;
          o.x = b.x + nx * (ORB_R + b.r + 0.5);
          o.y = b.y + ny * (ORB_R + b.r + 0.5);
          if (Math.abs(o.vx) > MAX_V + 3) o.vx = Math.sign(o.vx) * (MAX_V + 3);
          if (o.vy < MAX_UP) o.vy = MAX_UP;
          if (o.vy > MAX_V + 3) o.vy = MAX_V + 3;
          b.lastHit = state.tick;
          o.bumperHits += 1;
        }
      }
      // Spinners — rotating bars that SLAM orbs. Line-segment collision using
      // current position; then add tangential kick based on angular velocity.
      for (const sp of spinners) {
        const abx = sp._x2 - sp._x1, aby = sp._y2 - sp._y1;
        const apx = o.x - sp._x1, apy = o.y - sp._y1;
        const len2 = abx * abx + aby * aby || 1;
        let t = (apx * abx + apy * aby) / len2;
        t = Math.max(0, Math.min(1, t));
        const cx = sp._x1 + abx * t, cy = sp._y1 + aby * t;
        const dx = o.x - cx, dy = o.y - cy;
        const d = Math.hypot(dx, dy);
        const thickness = sp.thickness ?? 6;
        if (d < ORB_R + thickness) {
          const nx = dx / (d || 1), ny = dy / (d || 1);
          const vn = o.vx * nx + o.vy * ny;
          if (vn < 0) {
            const j = 2.0 * vn;   // super-elastic
            o.vx -= j * nx;
            o.vy -= j * ny;
          }
          // Tangential kick from spinning: velocity at impact point =
          // omega × radius, perpendicular to the radius from spinner pivot.
          const rx = cx - sp.cx, ry = cy - sp.cy;
          // Tangent direction (rotated 90° from radius, matching omega sign)
          const tx = -ry, ty = rx;
          const norm = Math.hypot(tx, ty) || 1;
          const speedAtImpact = sp._omega * Math.hypot(rx, ry) * 60; // per second-ish
          const kick = Math.min(6, Math.abs(speedAtImpact) * 0.4);
          o.vx += (tx / norm) * kick * Math.sign(sp._omega);
          o.vy += (ty / norm) * kick * Math.sign(sp._omega);
          // Cap
          if (Math.abs(o.vx) > MAX_V + 4) o.vx = Math.sign(o.vx) * (MAX_V + 4);
          if (o.vy < MAX_UP - 1) o.vy = MAX_UP - 1;
          if (o.vy > MAX_V + 4) o.vy = MAX_V + 4;
          o.x = cx + nx * (ORB_R + thickness + 0.5);
          o.y = cy + ny * (ORB_R + thickness + 0.5);
          sp.lastHit = state.tick;
        }
      }
      // Holes — use animated position (_x). Warp is instant.
      for (const h of holes) {
        const dx = o.x - h._x, dy = o.y - h.y;
        if (dx * dx + dy * dy < (h.r - 2) * (h.r - 2)) {
          const exitX = 40 + holeRng() * (W - 80);
          const exitY = Math.min(FINISH_Y - 60, h.y + h.warpDy);
          o.trapped = HOLE_TRAP_TICKS;
          o.trapExit = { x: exitX, y: exitY };
          o.vx = 0;
          o.vy = 0;
          o.x = h._x;
          o.y = h.y;
          h.lastHit = state.tick;
          break;
        }
      }
      // Orb-orb collisions — nearly-elastic so the pack really bangs around.
      for (const other of orbs) {
        if (other === o || other.finished || other.trapped > 0) continue;
        const adx = o.x - other.x;
        if (adx > ORB_R * 2 || adx < -ORB_R * 2) continue;
        const ady = o.y - other.y;
        if (ady > ORB_R * 2 || ady < -ORB_R * 2) continue;
        const d = Math.hypot(adx, ady);
        if (d > 0 && d < ORB_R * 2) {
          const nx = adx / d, ny = ady / d;
          const overlap = (ORB_R * 2 - d) / 2;
          o.x += nx * overlap; o.y += ny * overlap;
          other.x -= nx * overlap; other.y -= ny * overlap;
          const dv = (o.vx - other.vx) * nx + (o.vy - other.vy) * ny;
          if (dv < 0) {
            // Restitution 0.95 → almost elastic. Each orb gets half the impulse
            // (equal mass), so the visible effect is a real BONK.
            const impulse = dv * 0.95;
            o.vx -= impulse * nx; o.vy -= impulse * ny;
            other.vx += impulse * nx; other.vy += impulse * ny;
          }
        }
      }
      // Finish line
      if (o.y >= FINISH_Y) {
        o.finished = true;
        o.finishedAt = state.tick;
        if (onFinish) onFinish(o);
      }
    }
  }
  state.tick += 1;
}

// ---------------------- Component ----------------------
export default function OrbDrop({ ws, isStreamer, channelUsername }) {
  const { user, refreshMe } = useAuth();
  const canvasRef = useRef(null);
  const [orbState, setOrbState] = useState({ status: "idle" });
  const [selectedTrack, setSelectedTrack] = useState("classic");
  const [errMsg, setErrMsg] = useState("");
  const [finishOrder, setFinishOrder] = useState([]);
  const [, setMyPeerId] = useState(null);
  const winnerReportedRef = useRef(false);
  const simRef = useRef(null);
  const cameraRef = useRef(0);
  const rafRef = useRef(null);
  const finishOrderRef = useRef([]);
  const raceStartRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!ws) return;
    const onMsg = (e) => {
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      if (m.type === "welcome") setMyPeerId(m.peer_id);
      if (m.type === "orbdrop_state") {
        setOrbState(m.state);
        if (m.state.status === "lobby" || m.state.status === "idle") {
          setFinishOrder([]);
          finishOrderRef.current = [];
          winnerReportedRef.current = false;
        }
        if (m.state.status === "finished" && m.state.winner_username && m.state.winner_username === user?.username) {
          toast.success(`You won Orb Drop! +5 Sparks credited.`, { duration: 5000 });
          refreshMe?.();
        }
      }
      if (m.type === "orbdrop_error") {
        setErrMsg(m.error);
        toast.error(m.error);
        setTimeout(() => setErrMsg(""), 3000);
      }
    };
    ws.addEventListener("message", onMsg);
    if (ws.readyState === 1) ws.send(JSON.stringify({ type: "orbdrop_sync_request" }));
    return () => ws.removeEventListener("message", onMsg);
  }, [ws, user?.username, refreshMe]);

  const send = useCallback((type, extra = {}) => {
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type, ...extra }));
  }, [ws]);

  const openLobby = () => send("orbdrop_open", { track: selectedTrack });
  const joinRace = () => send("orbdrop_join");
  const startRace = () => send("orbdrop_start");
  const resetRace = () => send("orbdrop_reset");

  const status = orbState.status || "idle";

  // Stable refs for the render loop — the RAF loop reads these WITHOUT being
  // re-created on every server broadcast (which would tear down / restart the
  // loop and cause visible glitches).
  const statusRef = useRef(status);
  const orbStateRef = useRef(orbState);
  const isStreamerRef = useRef(isStreamer);
  const sendRef = useRef(send);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { orbStateRef.current = orbState; }, [orbState]);
  useEffect(() => { isStreamerRef.current = isStreamer; }, [isStreamer]);
  useEffect(() => { sendRef.current = send; }, [send]);

  // Build sim when status flips to racing
  useEffect(() => {
    if (orbState.status !== "racing" || !orbState.seed || !orbState.participants) {
      simRef.current = null;
      cameraRef.current = 0;
      finishOrderRef.current = [];
      // Preserve visible finishOrder while in "finished" so the winner banner
      // + finish-order UI stays populated.
      if (orbState.status !== "finished") setFinishOrder([]);
      winnerReportedRef.current = false;
      return;
    }
    simRef.current = simulate({
      seed: orbState.seed,
      track: orbState.track,
      participants: orbState.participants,
    });
    cameraRef.current = 0;
    finishOrderRef.current = [];
    setFinishOrder([]);
    winnerReportedRef.current = false;
    raceStartRef.current = performance.now();
    setElapsed(0);
  }, [orbState.status, orbState.seed, orbState.track, orbState.participants]);

  // Physics + render loop — mounts ONCE and reads via refs so server-state
  // broadcasts don't restart the RAF (which would cause visible glitches).
  useEffect(() => {
    let mounted = true;
    const loop = () => {
      if (!mounted) return;
      rafRef.current = requestAnimationFrame(loop);
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      const sim = simRef.current;
      const st = statusRef.current;
      const os = orbStateRef.current;
      if (st === "racing" && sim) {
        step(sim, (o) => {
          finishOrderRef.current.push({ peer_id: o.peer_id, username: o.username, color: o.color });
          setFinishOrder([...finishOrderRef.current]);
          if (finishOrderRef.current.length === 1 && isStreamerRef.current && !winnerReportedRef.current) {
            winnerReportedRef.current = true;
            sendRef.current("orbdrop_finish", { winner_peer: o.peer_id });
          }
        });
        let leaderY = 0;
        for (const o of sim.orbs) if (!o.finished && o.y > leaderY) leaderY = o.y;
        const target = Math.max(0, Math.min(WORLD_H - H, leaderY - H * 0.55));
        cameraRef.current += (target - cameraRef.current) * 0.08;
        setElapsed(Math.floor((performance.now() - raceStartRef.current) / 1000));
        renderRacing(ctx, sim, cameraRef.current);
      } else {
        renderIdle(ctx, os);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { mounted = false; cancelAnimationFrame(rafRef.current); };
  }, []);

  const participants = orbState.participants || [];
  const iAmIn = participants.some(p => p.username === user?.username);
  const canJoin = user && !iAmIn && status === "lobby" && participants.length < 1000;

  return (
    <div className="sc-card p-5" data-testid="orbdrop-game">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Party · Massive Multiplayer</div>
          <h2 className="sc-heading text-2xl font-black flex items-center gap-2">
            <Zap className="w-6 h-6 text-[color:var(--sc-cyan)]" />
            Orb Drop
          </h2>
          <div className="text-xs text-zinc-400 mt-1">Everyone drops. Physics decides. Winner gets <span className="text-[color:var(--sc-volt)] font-bold">5 Sparks</span>.</div>
        </div>
        <div className="flex items-center gap-3 sc-mono text-xs">
          <div className="flex items-center gap-1.5" data-testid="orbdrop-count">
            <Users className="w-3.5 h-3.5 text-[color:var(--sc-cyan)]" />
            <span className="text-white font-bold">{participants.length}</span>
            <span className="text-zinc-500">/ 1000</span>
          </div>
          {status === "racing" && (
            <div className="px-2 py-0.5 rounded-sm border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/10 text-[color:var(--sc-volt)] font-bold" data-testid="orbdrop-timer">
              {elapsed}s
            </div>
          )}
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--sc-cyan)]" data-testid="orbdrop-status">{status}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[auto,1fr] gap-5">
        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            className="rounded-sm border border-white/10 shadow-[0_0_40px_rgba(0,229,255,0.25)]"
            data-testid="orbdrop-canvas"
          />
        </div>

        <div className="flex flex-col gap-4">
          {isStreamer && (
            <div className="border border-[color:var(--sc-pink)]/40 bg-[color:var(--sc-pink)]/5 rounded-sm p-3" data-testid="orbdrop-host-panel">
              <div className="sc-label mb-2 text-[color:var(--sc-pink)]">Host Controls</div>
              {status === "idle" ? (
                <div>
                  <div className="text-xs text-zinc-400 mb-2">Choose a track and open a lobby.</div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {["classic", "zigzag", "funnel"].map(t => (
                      <button
                        key={t}
                        data-testid={`orbdrop-track-${t}`}
                        onClick={() => setSelectedTrack(t)}
                        className={`px-3 py-1.5 text-xs rounded-sm border transition ${selectedTrack === t ? "border-[color:var(--sc-cyan)] bg-[color:var(--sc-cyan)]/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}
                      >
                        {t[0].toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                  <button data-testid="orbdrop-open-lobby" onClick={openLobby} className="sc-btn text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Open Lobby
                  </button>
                </div>
              ) : status === "finished" ? (
                <div>
                  <div className="text-xs text-zinc-400 mb-2">Round complete. Reset to open a new lobby.</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {["classic", "zigzag", "funnel"].map(t => (
                      <button
                        key={t}
                        data-testid={`orbdrop-track-${t}`}
                        onClick={() => setSelectedTrack(t)}
                        className={`px-3 py-1.5 text-xs rounded-sm border transition ${selectedTrack === t ? "border-[color:var(--sc-cyan)] bg-[color:var(--sc-cyan)]/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}
                      >
                        {t[0].toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="orbdrop-reset-game" onClick={resetRace} className="sc-btn text-xs flex items-center gap-1.5" style={{ background: "var(--sc-volt)", color: "#000" }}>
                      <RotateCcw className="w-3.5 h-3.5" /> Reset Game
                    </button>
                    <button data-testid="orbdrop-reset-and-open" onClick={openLobby} className="sc-btn-ghost text-xs flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> Reset & Open New Lobby
                    </button>
                  </div>
                </div>
              ) : status === "lobby" ? (
                <div className="flex flex-wrap gap-2">
                  <button data-testid="orbdrop-start-race" onClick={startRace} disabled={participants.length < 1} className="sc-btn text-xs flex items-center gap-1.5 disabled:opacity-40">
                    <Play className="w-3.5 h-3.5" /> Start Race ({participants.length})
                  </button>
                  <button data-testid="orbdrop-cancel-lobby" onClick={resetRace} className="sc-btn-ghost text-xs flex items-center gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5" /> Cancel Lobby
                  </button>
                </div>
              ) : (
                <div>
                  <div className="text-xs text-zinc-400 mb-2">Race in progress on <span className="text-[color:var(--sc-cyan)]">{orbState.track}</span> track…</div>
                  <button data-testid="orbdrop-force-reset" onClick={resetRace} className="sc-btn-ghost text-xs flex items-center gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5" /> Force Reset
                  </button>
                </div>
              )}
            </div>
          )}

          {!isStreamer && (
            <div className="border border-[color:var(--sc-cyan)]/30 bg-[color:var(--sc-cyan)]/5 rounded-sm p-3">
              <div className="sc-label mb-2 text-[color:var(--sc-cyan)]">Your Turn</div>
              {status === "idle" && <div className="text-xs text-zinc-400">Waiting for the streamer to open a lobby…</div>}
              {status === "lobby" && (
                iAmIn ? (
                  <div className="text-xs text-[color:var(--sc-volt)]">✓ You&apos;re in! Waiting for the race to start.</div>
                ) : !user ? (
                  <div className="text-xs text-zinc-400">Log in to join Orb Drop.</div>
                ) : (
                  <button data-testid="orbdrop-join-race" onClick={joinRace} disabled={!canJoin} className="sc-btn text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Drop In
                  </button>
                )
              )}
              {status === "racing" && (
                iAmIn ? <div className="text-xs text-[color:var(--sc-cyan)]">You&apos;re racing! Watch your orb glow.</div>
                     : <div className="text-xs text-zinc-400">Race in progress — join the next round.</div>
              )}
              {status === "finished" && <div className="text-xs text-zinc-400">Race finished. Wait for the next drop.</div>}
            </div>
          )}

          {(status === "lobby" || status === "racing") && participants.length > 0 && (
            <div className="border border-white/10 rounded-sm p-3 max-h-40 overflow-auto" data-testid="orbdrop-participants">
              <div className="sc-label mb-2">Racers</div>
              <div className="flex flex-wrap gap-1.5">
                {participants.map(p => (
                  <div key={p.peer_id} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-white/10 bg-black/40" style={{ boxShadow: `0 0 6px ${p.color}55` }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: p.color, boxShadow: `0 0 4px ${p.color}` }} />
                    <span className="text-zinc-200">{p.username}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {status === "finished" && orbState.winner_username && (
            <div className="border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/10 rounded-sm p-4 text-center" data-testid="orbdrop-winner-banner">
              <Trophy className="w-8 h-8 text-[color:var(--sc-volt)] mx-auto mb-2" />
              <div className="sc-heading text-lg font-black text-[color:var(--sc-volt)]">{orbState.winner_username} wins!</div>
              <div className="text-xs text-zinc-300 mt-1">+5 Sparks credited</div>
              {isStreamer && (
                <button data-testid="orbdrop-new-round" onClick={resetRace} className="sc-btn-ghost text-xs mt-3 mx-auto flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> New Round
                </button>
              )}
            </div>
          )}

          {status === "racing" && finishOrder.length > 0 && (
            <div className="border border-white/10 rounded-sm p-3" data-testid="orbdrop-podium">
              <div className="sc-label mb-2 text-[color:var(--sc-volt)]">Podium</div>
              <ol className="text-xs space-y-1">
                {finishOrder.slice(0, 5).map((o, i) => (
                  <li key={o.peer_id} className="flex items-center gap-2">
                    <span className="w-5 text-zinc-500 sc-mono">{i + 1}.</span>
                    <span className="w-2 h-2 rounded-full" style={{ background: o.color, boxShadow: `0 0 4px ${o.color}` }} />
                    <span className="text-zinc-200">{o.username}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {errMsg && <div className="text-[color:var(--sc-pink)] text-xs">{errMsg}</div>}
        </div>
      </div>

      <div className="text-[11px] text-zinc-500 text-center mt-4">
        Neon race track · super-elastic orb-vs-orb bounces · spinners · moving platforms · drifting warp holes. Winner takes 5 Sparks.
      </div>
    </div>
  );
}

// ---------------------- Rendering helpers ----------------------
function drawBackground(ctx, cam) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#050510");
  g.addColorStop(1, "#0a0a20");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Parallax dot grid
  ctx.fillStyle = "rgba(255,255,255,0.03)";
  const offset = (cam * 0.3) % 24;
  for (let y = -offset; y < H; y += 24) for (let x = 20; x < W; x += 24) ctx.fillRect(x, y, 1, 1);
}

function drawFinish(ctx, cam) {
  const y = FINISH_Y - cam;
  if (y < -20 || y > H + 20) return;
  ctx.fillStyle = "rgba(204,255,0,0.08)";
  ctx.fillRect(10, y, W - 20, H - y);
  ctx.strokeStyle = "#CCFF00";
  ctx.shadowBlur = 14;
  ctx.shadowColor = "#CCFF00";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(10, y);
  ctx.lineTo(W - 10, y);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#CCFF00";
  ctx.font = "bold 11px monospace";
  ctx.textAlign = "center";
  ctx.fillText("FINISH", W / 2, y + 18);
}

function drawPegs(ctx, pegs, cam) {
  ctx.fillStyle = "#00E5FF";
  ctx.shadowBlur = 5; ctx.shadowColor = "#00E5FF";
  for (const p of pegs) {
    const y = p.y - cam;
    if (y < -10 || y > H + 10) continue;
    ctx.beginPath(); ctx.arc(p.x, y, p.r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.shadowBlur = 0;
}

function drawWalls(ctx, walls, cam) {
  ctx.lineWidth = 3;
  for (const w of walls) {
    const x1 = w._x1 ?? w.x1, y1 = (w._y1 ?? w.y1) - cam;
    const x2 = w._x2 ?? w.x2, y2 = (w._y2 ?? w.y2) - cam;
    if ((y1 < -20 && y2 < -20) || (y1 > H + 20 && y2 > H + 20)) continue;
    if (w.motion) {
      // Moving platform — cyan glow to signal "this one moves"
      ctx.strokeStyle = "rgba(0,229,255,0.85)";
      ctx.shadowBlur = 12; ctx.shadowColor = "#00E5FF";
    } else {
      ctx.strokeStyle = "rgba(255,51,102,0.55)";
      ctx.shadowBlur = 6; ctx.shadowColor = "rgba(255,51,102,0.5)";
    }
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

function drawSpinners(ctx, spinners, tick, cam) {
  for (const sp of spinners) {
    const cy = sp.cy - cam;
    if (cy < -60 || cy > H + 60) continue;
    // Pivot dot
    ctx.fillStyle = "#222";
    ctx.beginPath(); ctx.arc(sp.cx, cy, 4, 0, Math.PI * 2); ctx.fill();
    // Bar (use animated endpoints if available, else compute now)
    const x1 = sp._x1 ?? sp.cx + Math.cos(sp.phase) * sp.len / 2;
    const y1 = (sp._y1 ?? sp.cy + Math.sin(sp.phase) * sp.len / 2) - cam;
    const x2 = sp._x2 ?? sp.cx - Math.cos(sp.phase) * sp.len / 2;
    const y2 = (sp._y2 ?? sp.cy - Math.sin(sp.phase) * sp.len / 2) - cam;
    const hitGlow = Math.max(0, 1 - (tick - (sp.lastHit || -999)) / 6);
    ctx.strokeStyle = sp.color;
    ctx.shadowBlur = 12 + hitGlow * 24;
    ctx.shadowColor = sp.color;
    ctx.lineWidth = (sp.thickness ?? 6) * 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    // Bright caps at each tip
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 10;
    ctx.beginPath(); ctx.arc(x1, y1, 3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x2, y2, 3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.lineCap = "butt";
  }
}

function drawBumpers(ctx, bumpers, tick, cam) {
  for (const b of bumpers) {
    const y = b.y - cam;
    if (y < -30 || y > H + 30) continue;
    const glow = Math.max(0, 1 - (tick - (b.lastHit || -999)) / 8);
    ctx.shadowBlur = 12 + glow * 24;
    ctx.shadowColor = b.color;
    ctx.fillStyle = b.color;
    ctx.beginPath(); ctx.arc(b.x, y, b.r + glow * 3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    // inner dark
    ctx.fillStyle = "#050510";
    ctx.beginPath(); ctx.arc(b.x, y, b.r - 6, 0, Math.PI * 2); ctx.fill();
    // dot
    ctx.fillStyle = b.color;
    ctx.beginPath(); ctx.arc(b.x, y, 3, 0, Math.PI * 2); ctx.fill();
  }
}

function drawHoles(ctx, holes, tick, cam) {
  for (const h of holes) {
    const hx = h._x ?? h.x;
    const y = h.y - cam;
    if (y < -30 || y > H + 30) continue;
    // Outer glowing ring (danger)
    const pulse = 0.5 + 0.5 * Math.sin(tick * 0.15);
    ctx.strokeStyle = h.motion ? "#22d3ee" : "#a855f7";
    ctx.shadowBlur = 10 + pulse * 8;
    ctx.shadowColor = h.motion ? "#22d3ee" : "#a855f7";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(hx, y, h.r + 2, 0, Math.PI * 2); ctx.stroke();
    ctx.shadowBlur = 0;
    // Inner void (radial gradient)
    const g = ctx.createRadialGradient(hx, y, 0, hx, y, h.r);
    g.addColorStop(0, "#000000");
    g.addColorStop(0.7, h.motion ? "#001a33" : "#1a0033");
    g.addColorStop(1, h.motion ? "rgba(34,211,238,0.4)" : "rgba(168,85,247,0.4)");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(hx, y, h.r, 0, Math.PI * 2); ctx.fill();
    // Swirl marks
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1;
    for (let k = 0; k < 3; k++) {
      const a = tick * 0.05 + k * (Math.PI * 2 / 3);
      ctx.beginPath();
      ctx.arc(hx, y, h.r * 0.55, a, a + Math.PI * 0.4);
      ctx.stroke();
    }
    // Motion trail arrow for drifting holes
    if (h.motion) {
      const dir = Math.cos(tick / 60 * h.motion.freq * 60 + h.motion.phase);
      ctx.fillStyle = "rgba(34,211,238,0.4)";
      ctx.font = "bold 12px monospace";
      ctx.textAlign = "center";
      ctx.fillText(dir >= 0 ? "→" : "←", hx, y - h.r - 6);
    }
  }
}

function drawOrbs(ctx, orbs, cam) {
  for (const o of orbs) {
    const y = o.y - cam;
    if (o.finished && (y < -20 || y > H + 20)) continue;
    // Trapped orbs render dim
    ctx.globalAlpha = o.trapped > 0 ? 0.35 : (o.finished ? 0.5 : 1);
    ctx.shadowBlur = o.finished ? 6 : 14;
    ctx.shadowColor = o.color;
    const g = ctx.createRadialGradient(o.x - 1.5, y - 2, 0.5, o.x, y, ORB_R);
    g.addColorStop(0, "#ffffff");
    g.addColorStop(1, o.color);
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(o.x, y, ORB_R, 0, Math.PI * 2); ctx.fill();
  }
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
}

function drawProgressBar(ctx, sim) {
  // Right-edge vertical progress bar showing leader position + finish tick
  const barX = W - 6;
  ctx.fillStyle = "rgba(255,255,255,0.05)";
  ctx.fillRect(barX, 10, 3, H - 20);
  let leaderY = 0;
  for (const o of sim.orbs) if (!o.finished && o.y > leaderY) leaderY = o.y;
  const frac = Math.min(1, leaderY / FINISH_Y);
  ctx.fillStyle = "#CCFF00";
  ctx.shadowBlur = 6; ctx.shadowColor = "#CCFF00";
  ctx.fillRect(barX, 10, 3, (H - 20) * frac);
  ctx.shadowBlur = 0;
}

function renderIdle(ctx, state) {
  drawBackground(ctx, 0);
  const { pegs, walls, bumpers, holes, spinners } = buildTrack(state.track || "classic");
  // Pre-populate _fields so idle preview renders spinners at their start angle
  for (const w of walls) {
    if (w.motion) { w._x1 = w.x1; w._y1 = w.y1; w._x2 = w.x2; w._y2 = w.y2; }
  }
  for (const sp of spinners) {
    const cA = Math.cos(sp.phase), sA = Math.sin(sp.phase);
    sp._x1 = sp.cx + cA * sp.len / 2; sp._y1 = sp.cy + sA * sp.len / 2;
    sp._x2 = sp.cx - cA * sp.len / 2; sp._y2 = sp.cy - sA * sp.len / 2;
  }
  drawWalls(ctx, walls, 0);
  drawPegs(ctx, pegs, 0);
  drawBumpers(ctx, bumpers, 0, 0);
  drawSpinners(ctx, spinners, 0, 0);
  drawHoles(ctx, holes, 0, 0);

  ctx.textAlign = "center";

  // Finished — render a big winner screen so the streamer + viewers see a
  // clean end-of-race splash instead of the stale last racing frame.
  if (state.status === "finished") {
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#CCFF00";
    ctx.shadowBlur = 18; ctx.shadowColor = "#CCFF00";
    ctx.font = "bold 22px monospace";
    ctx.fillText("🏆 WINNER 🏆", W / 2, H / 2 - 20);
    ctx.font = "bold 26px monospace";
    ctx.fillText((state.winner_username || "").toUpperCase(), W / 2, H / 2 + 14);
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "11px monospace";
    ctx.fillText("New round starting soon…", W / 2, H / 2 + 42);
    return;
  }

  ctx.fillStyle = "#00E5FF";
  ctx.shadowBlur = 12; ctx.shadowColor = "#00E5FF";
  ctx.font = "bold 26px monospace";
  ctx.fillText("ORB DROP", W / 2, 60);
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "12px monospace";
  const label = state.status === "lobby" ? `Lobby open — ${state.participants?.length || 0} racers` : "Waiting for host";
  ctx.fillText(label, W / 2, 88);
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.font = "10px monospace";
  ctx.fillText(`Track: ${(state.track || "classic").toUpperCase()} · spinners · moving platforms · drifting warps`, W / 2, 108);
}

function renderRacing(ctx, sim, cam) {
  drawBackground(ctx, cam);
  drawWalls(ctx, sim.walls, cam);
  drawPegs(ctx, sim.pegs, cam);
  drawBumpers(ctx, sim.bumpers, sim.tick, cam);
  drawSpinners(ctx, sim.spinners, sim.tick, cam);
  drawHoles(ctx, sim.holes, sim.tick, cam);
  drawFinish(ctx, cam);
  drawOrbs(ctx, sim.orbs, cam);
  drawProgressBar(ctx, sim);
}
